import '../../../Components/Form/form.css'

import { useEffect, useRef, useState } from 'react'

import { Modal } from '../../../Components'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { getServiceCardTitle } from './Functions'
import { Icon } from './Icons'
import { TransferField } from './TransferField'

const language = cap.language.state || 'IT'
const t = translations[language]

/**
 * Conta i campi con errore all'interno di una card servizio (PrimeReact e nuovi field).
 * @param {HTMLElement|null} root - Nodo radice della card
 * @returns {number} Numero di campi invalidi o mancanti
 */
const countCardFieldErrors = (root) => {
  if (!root) return 0
  const fieldErrors = root.querySelectorAll('.ff-field[data-state="error"]').length
  let inputErrors = 0
  root.querySelectorAll('.p-invalid').forEach((el) => {
    if (!el.closest('.ff-field[data-state="error"]')) inputErrors += 1
  })
  return fieldErrors + inputErrors
}

/**
 * Stato vuoto per un pannello servizi senza voci inserite.
 * @param {Object} props
 * @param {string} props.icon - Nome icona (vedi Icons.jsx)
 * @param {string} props.title - Messaggio principale
 * @param {string} [props.hint] - Suggerimento secondario
 */
export const ServiceEmptyState = ({ icon = 'grid', title, hint }) => (
  <div className="gf-service-empty" role="status" aria-label={title}>
    <span className="gf-service-empty__icon" aria-hidden="true">
      <Icon name={icon} size={22} />
    </span>
    <p className="gf-service-empty__title">{title}</p>
    {hint ? <p className="gf-service-empty__hint">{hint}</p> : null}
  </div>
)

export const CustomHeader = ({ title }) => (
  <h3 className="gf-page-title" style={{ margin: 0 }}>
    {title}
  </h3>
)

export const SingleServiceContainer = ({
  children,
  onRemove,
  readOnly,
  index,
  serviceType,
  state,
  title: titleProp
}) => {
  const [modalVisible, setModalVisible] = useState(false)
  const cardRef = useRef(null)
  const [errorCount, setErrorCount] = useState(0)

  const title =
    titleProp ||
    (serviceType && state != null ? getServiceCardTitle(state, serviceType, index) : null) ||
    (typeof index === 'number' ? `# ${index + 1}` : '')

  useEffect(() => {
    const root = cardRef.current
    if (!root) return

    const syncErrors = () => {
      const count = countCardFieldErrors(root)
      setErrorCount((prev) => (prev === count ? prev : count))
    }

    syncErrors()
    const observer = new MutationObserver(syncErrors)
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-state']
    })
    return () => observer.disconnect()
  }, [children])

  const hasErrors = errorCount > 0

  return (
    <div ref={cardRef} className={`ff-service-card${hasErrors ? ' has-error' : ''}`}>
      <ConfirmModal
        header={t.deleteServiceHeader}
        label={t.gfDeleteServiceLabelIrreversible}
        onConfirm={onRemove}
        visible={[modalVisible, setModalVisible]}
        tone="danger"
        confirmLabel={t.gfEliminaServizio}
      />
      <div className="ff-service-card__header">
        <div className="ff-service-card__title-row">
          <span className="ff-service-card__index">{title}</span>
          {hasErrors && (
            <span className="ff-service-card__error-msg">
              <Icon name="alert" size={12} />
              {errorCount}{' '}
              {errorCount === 1 ? t.gfCampoObbligatorioMancante : t.gfCampiObbligatoriMancanti}
            </span>
          )}
        </div>
      </div>
      {!readOnly && (
        <button
          type="button"
          className="ff-service-card__remove"
          aria-label={`${t.gfRimuoviServizio} ${typeof index === 'number' ? index + 1 : ''}`}
          title={t.gfRimuovi}
          onClick={() => setModalVisible(true)}
        >
          <i className="pi pi-times" />
        </button>
      )}
      <div className="ff-service-grid">{children}</div>
    </div>
  )
}

export const AddServiceButton = ({ label, onClick, disabled }) => (
  <button
    type="button"
    className="ff-add-button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
  >
    <i className="pi pi-plus" aria-hidden="true" />
    <span>{label}</span>
  </button>
)

/**
 * Dialog di conferma condiviso per il form Gigi
 * @param {Object} props
 * @param {[boolean, Function]} props.visible - Stato visibilità [visible, setVisible]
 * @param {string} props.header - Titolo del dialog
 * @param {string} props.label - Messaggio principale
 * @param {Function} props.onConfirm - Callback conferma
 * @param {string} [props.confirmLabel] - Etichetta pulsante conferma
 * @param {string} [props.cancelLabel] - Etichetta pulsante annulla
 * @param {'default'|'danger'} [props.tone] - Variante visiva
 * @param {boolean} [props.isLoading] - Mostra spinner sul pulsante conferma
 */
export const ConfirmModal = ({
  visible,
  header,
  label,
  onConfirm,
  confirmLabel,
  cancelLabel,
  tone = 'default',
  isLoading = false
}) => {
  const [modalVisible, setModalVisible] = visible
  const isDanger = tone === 'danger'
  const cancel = cancelLabel || t.annulla
  const confirm = confirmLabel || t.conferma

  const handleConfirm = () => {
    onConfirm()
    setModalVisible(false)
  }

  return (
    <Modal
      visible={modalVisible}
      header={header}
      className={`gf-dialog-modal${isDanger ? ' gf-dialog-modal--danger' : ''}`}
      onHide={() => setModalVisible(false)}
      style={{ width: 'min(440px, 92vw)' }}
      contentStyle={{ padding: 0 }}
      closable={!isLoading}
      footer={
        <div className="gf-dialog-modal__footer">
          <button
            type="button"
            className="gf-btn"
            onClick={() => setModalVisible(false)}
            disabled={isLoading}
          >
            {cancel}
          </button>
          <button
            type="button"
            className={`gf-btn${isDanger ? ' gf-btn--danger' : ' gf-btn--primary'}`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? <span className="gf-btn__spinner" aria-hidden="true" /> : null}
            {confirm}
          </button>
        </div>
      }
    >
      <div className="gf-dialog-modal__body gf-dialog-modal__body--compact">
        <div className="gf-dialog-modal__confirm">
          <span className="gf-dialog-modal__icon" aria-hidden="true">
            <Icon name={isDanger ? 'alert' : 'info'} size={18} />
          </span>
          <p className="gf-dialog-modal__message">{label}</p>
        </div>
      </div>
    </Modal>
  )
}

export { TransferField }
